import { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import MainLayout from './components/Layout/MainLayout';
import SuperAdminLayout from './components/Layout/SuperAdminLayout';
import LoginPage from './components/Auth/LoginPage';
import SuperAdminLoginPage from './components/Auth/SuperAdminLoginPage';
import CustomerLoginPage from './components/Auth/CustomerLoginPage';
import CustomerPrivateRoute from './components/Auth/CustomerPrivateRoute';
import ForgotPasswordPage from './components/Auth/ForgotPasswordPage';
import ResetPasswordPage from './components/Auth/ResetPasswordPage';
import AgentSettings from './components/Settings/AgentSettings';
import AgentDetail from './components/Settings/AgentDetail';
import DepartmentSettings from './components/Settings/DepartmentSettings';
import ContractSettings from './components/Settings/ContractSettings';
import CustomerSettings from './components/Settings/CustomerSettings';
import ContractDetail from './components/Settings/ContractDetail';
import CustomerDetail from './components/Settings/CustomerDetail';
import SlaSettings from './components/Settings/SlaSettings';
import SlaDetail from './components/Settings/SlaDetail';
import SmtpSettings from './components/Settings/SmtpSettings';
import HolidaySettings from './components/Settings/HolidaySettings';
import ProjectSettings from './components/Settings/ProjectSettings';
import ProjectDetail from './components/Settings/ProjectDetail';
import SupportCasesPage from './pages/SupportCasesPage';
import SupportCaseDetailPage from './pages/SupportCaseDetailPage';
import DashboardPage from './pages/DashboardPage';
import SuperAdminDashboardPage from './pages/SuperAdminDashboardPage';
import SuperAdminTenantsPage from './pages/SuperAdminTenantsPage';
import SuperAdminTenantDetailPage from './pages/SuperAdminTenantDetailPage';
import CustomerDashboardPage from './pages/customer/CustomerDashboardPage';
import CustomerSupportCasesPage from './pages/customer/CustomerSupportCasesPage';
import CustomerReportsPage from './pages/customer/CustomerReportsPage';
import CustomerSupportCaseDetailPage from './pages/customer/CustomerSupportCaseDetailPage';
import CompanyReportPage from './pages/reports/CompanyReportPage';
import AgentReportPage from './pages/reports/AgentReportPage';
import ProjectReportPage from './pages/reports/ProjectReportPage';
import TicketsReportPage from './pages/reports/TicketsReportPage';
import SLAReportPage from './pages/reports/SLAReportPage';
import SLAReportByAgentPage from './pages/reports/SLAReportByAgentPage';
import SLAReportByCustomerPage from './pages/reports/SLAReportByCustomerPage';
import ProductReportPage from './pages/reports/ProductReportPage';
import StatisticsPage from './pages/StatisticsPage';
import TimesheetPage from './pages/TimesheetPage';
import TimesheetCompanyReportPage from './pages/TimesheetCompanyReportPage';
import TimesheetUserReportPage from './pages/TimesheetUserReportPage';

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/admin" />;
};

const SuperAdminPrivateRoute = ({ children }) => {
  const token = localStorage.getItem('superadmin_token');
  return token ? children : <Navigate to="/superadmin" />;
};

function App() {
  // Oturum zaman aşımı: 5 dk idle, son 60 sn uyarı popup
  const IDLE_LIMIT_MS = 5 * 60 * 1000;
  const WARNING_MS = 60 * 1000;
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(60);
  const warningIntervalRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const logoutTimeoutRef = useRef(null);

  const clearTimers = () => {
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current);
      warningIntervalRef.current = null;
    }
  };

  const doLogout = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('superadmin_token');
      localStorage.removeItem('customer_token');
      localStorage.removeItem('user');
      // Diğer sekmeleri de çıkışa zorla
      localStorage.setItem('app_force_logout', String(Date.now()));
    } catch (e) {}
    window.location.href = '/';
  };

  const startWarningCountdown = () => {
    setShowIdleWarning(true);
    setRemainingSeconds(Math.floor(WARNING_MS / 1000));
    if (warningIntervalRef.current) clearInterval(warningIntervalRef.current);
    warningIntervalRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(warningIntervalRef.current);
          warningIntervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const resetIdleTimers = () => {
    // Kullanıcı etkileşimi olduğunda uyarıyı kapat ve zamanlayıcıları sıfırla
    setShowIdleWarning(false);
    setRemainingSeconds(60);
    clearTimers();
    // Uyarıyı IDLE_LIMIT_MS - WARNING_MS sonra göster
    warningTimeoutRef.current = setTimeout(startWarningCountdown, IDLE_LIMIT_MS - WARNING_MS);
    // Tam 5 dakika sonunda logout
    logoutTimeoutRef.current = setTimeout(doLogout, IDLE_LIMIT_MS);
  };

  useEffect(() => {
    // Sekmeler arası senkron çıkış
    const onStorage = (e) => {
      if (e.key === 'app_force_logout') {
        doLogout();
      }
    };

    // Kullanıcı etkileşimleri
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    activityEvents.forEach((evt) => window.addEventListener(evt, resetIdleTimers, { passive: true }));
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        resetIdleTimers();
      }
    });
    window.addEventListener('storage', onStorage);

    // Başlat
    resetIdleTimers();

    return () => {
      activityEvents.forEach((evt) => window.removeEventListener(evt, resetIdleTimers));
      window.removeEventListener('storage', onStorage);
      clearTimers();
    };
  }, []);

  return (
    <ThemeProvider>
      <LanguageProvider>
        <BrowserRouter>
        {showIdleWarning && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[90%] max-w-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Oturum Zaman Aşımı Uyarısı</h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                Herhangi bir işlem yapılmadığı için oturumunuz {remainingSeconds} saniye içinde sonlandırılacak.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={doLogout}
                  className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 text-sm"
                >
                  Hemen Çıkış Yap
                </button>
                <button
                  onClick={resetIdleTimers}
                  className="px-4 py-2 rounded-md bg-[#272c39] text-white hover:bg-[#1e2230] text-sm"
                >
                  Devam Et
                </button>
              </div>
            </div>
          </div>
        )}
        <Routes>
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="/admin" element={<LoginPage />} />
          <Route path="/superadmin" element={<SuperAdminLoginPage />} />
          <Route path="/login" element={<CustomerLoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Customer Routes */}
          <Route
            path="/customer-dashboard"
            element={
              <CustomerPrivateRoute>
                <CustomerDashboardPage />
              </CustomerPrivateRoute>
            }
          />
          <Route
            path="/customer-support-cases"
            element={
              <CustomerPrivateRoute>
                <CustomerSupportCasesPage />
              </CustomerPrivateRoute>
            }
          />
          <Route
            path="/customer-support-case/:id"
            element={
              <CustomerPrivateRoute>
                <CustomerSupportCaseDetailPage />
              </CustomerPrivateRoute>
            }
          />
          <Route
            path="/customer-reports"
            element={
              <CustomerPrivateRoute>
                <CustomerReportsPage />
              </CustomerPrivateRoute>
            }
          />
          
          {/* Admin Routes */}
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <MainLayout>
                  <Routes>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/settings/departments" element={<DepartmentSettings />} />
                    <Route path="/settings/agent" element={<AgentSettings />} />
                    <Route path="/settings/agent/:id" element={<AgentDetail />} />
                    <Route path="/settings/contract" element={<ContractSettings />} />
                    <Route path="/settings/contract/:id" element={<ContractDetail />} />
                    <Route path="/settings/customer" element={<CustomerSettings />} />
                    <Route path="/settings/customer/:id" element={<CustomerDetail />} />
                    <Route path="/settings/sla" element={<SlaSettings />} />
                    <Route path="/settings/sla/:id" element={<SlaDetail />} />
                    <Route path="/settings/smtp" element={<SmtpSettings />} />
                    <Route path="/settings/working-hours" element={<HolidaySettings />} />
                    <Route path="/projects" element={<ProjectSettings />} />
                  <Route path="/projects/:id" element={<ProjectDetail />} />
                    <Route path="/support-cases" element={<SupportCasesPage />} />
                    <Route path="/support-cases/:id" element={<SupportCaseDetailPage />} />
                                          <Route path="/reports/company" element={<CompanyReportPage />} />
                      <Route path="/reports/agent" element={<AgentReportPage />} />
                      <Route path="/reports/project" element={<ProjectReportPage />} />
                      <Route path="/reports/tickets" element={<TicketsReportPage />} />
                      <Route path="/reports/sla" element={<SLAReportPage />} />
                      <Route path="/reports/sla-by-agent" element={<SLAReportByAgentPage />} />
                      <Route path="/reports/sla-by-customer" element={<SLAReportByCustomerPage />} />
                      <Route path="/reports/products" element={<ProductReportPage />} />
                    <Route path="/statistics" element={<StatisticsPage />} />
                    <Route path="/timesheet" element={<TimesheetPage />} />
                    <Route path="/timesheet/company-report" element={<TimesheetCompanyReportPage />} />
                    <Route path="/timesheet/user-report" element={<TimesheetUserReportPage />} />
                    <Route path="/" element={<Navigate to="/support-cases" />} />
                  </Routes>
                </MainLayout>
              </PrivateRoute>
            }
          />

          {/* SuperAdmin Routes */}
          <Route
            path="/superadmin/*"
            element={
              <SuperAdminPrivateRoute>
                <SuperAdminLayout>
                  <Routes>
                    <Route path="/dashboard" element={<SuperAdminDashboardPage />} />
                    <Route path="/tenants" element={<SuperAdminTenantsPage />} />
                    <Route path="/tenants/:uuid" element={<SuperAdminTenantDetailPage />} />
                    <Route path="/" element={<Navigate to="/superadmin/dashboard" />} />
                  </Routes>
                </SuperAdminLayout>
              </SuperAdminPrivateRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App; 